import { Temporal } from "temporal-polyfill";

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const durationFormat = new Intl.DurationFormat(undefined, {
  minutes: "numeric",
  seconds: "numeric",
});

export function formatMinutes(seconds: number) {
  return durationFormat.format(
    Temporal.Duration.from({
      seconds: Math.floor(seconds),
    }).round({ largestUnit: "minute" }),
  );
}

/**
 * Formats a date as a relative time string (e.g., "5 minutes ago", "2 days ago")
 * @param date ISO date string
 * @returns Formatted relative time string
 */
export function distanceToNow(date: string) {
  const now = Temporal.Now.zonedDateTimeISO();
  const pastDate = Temporal.Instant.from(date).toZonedDateTimeISO(now.timeZoneId);

  // Calculate the difference between dates in various units
  const diffInSeconds = now.epochSeconds - pastDate.epochSeconds;
  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  // Calculate months and years difference
  let months = (now.year - pastDate.year) * 12 + (now.month - pastDate.month);

  // Adjust for day of month
  if (now.day < pastDate.day) {
    months -= 1;
  }

  const years = Math.floor(months / 12);

  // Less than 1 minute: "just now"
  if (minutes < 1) {
    return "Just now";
  }

  // Less than 1 hour: "X minutes ago"
  if (hours < 1) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  }

  // 1 hour to less than 1 day: "X hours ago"
  if (days < 1) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  }

  // 1 day to less than 1 week: "X days ago"
  if (days < 7) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  }

  // 1 week to less than 1 month: "X weeks ago"
  if (months < 1) {
    return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
  }

  // 1 month to less than 1 year: "X months ago"
  if (years < 1) {
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  }

  // 1 year or more: "X years ago"
  return `${years} year${years !== 1 ? "s" : ""} ago`;
}

/**
 * Formats a date as a month string (e.g., "2024-12", "2025-01")
 * @param date Temporal.PlainDate
 * @returns Formatted month string
 */
export function formatMonthString(date: Temporal.PlainDate) {
  return `${date.year}-${date.month.toString().padStart(2, "0")}`;
}

/**
 * Returns the number of months between two dates
 * @param from Start date
 * @param to End date
 * @returns Number of months between the two dates
 */
export function getMonthCount(from: Temporal.PlainDate, to: Temporal.PlainDate) {
  return from.until(to, { largestUnit: "month" }).months + 1;
}
